import React from 'react'
import { Link } from '@inertiajs/react'
import Layout from '../Layout'
import ResourceForm from '../../../Components/ResourceForm'
import FormFieldController from '../../../Components/FormFieldController'
import MapLocationPicker from '../../../Components/MapLocationPicker'
import BackLink from '../../../Components/BackLink'
import { positionSchema } from '../../../schemas'

export default function Edit({ account, accounts, navigation, position, position_types, position_categories, holes, sponsors }) {
  const isIndoor = position.category === 'IndoorPosition'

  // Filter position types based on category
  const getPositionTypeOptions = () => {
    const geoTypes = ['tee_box', 'green', 'halfway_house', 'clubhouse', 'pro_shop', 'bar', 'restaurant']
    const indoorTypes = ['dining_table', 'booth', 'bar_seat', 'terrace', 'private_room']

    const allowedTypes = isIndoor ? indoorTypes : geoTypes
    return position_types
      .filter(([_, value]) => allowedTypes.includes(value))
      .map(([label, value]) => ({ label, value }))
  }

  const holeOptions = [
    { label: 'Not associated with a hole', value: '' },
    ...holes
  ]

  const handleDownloadQR = () => {
    const blob = new Blob([position.qr_code_svg], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${position.name.toLowerCase().replace(/\s+/g, '-')}-qr.svg`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <Layout
      account={account}
      accounts={accounts}
      navigation={navigation}
      title={`Edit: ${position.name}`}
    >
      <BackLink href="/manage/positions">Back to Locations</BackLink>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <div className="lg:col-span-2">
          <div className="bg-white shadow rounded-lg">
            <ResourceForm
              schema={positionSchema}
              resource="position"
              endpoint={`/manage/positions/${position.id}`}
              method="patch"
              backLink="/manage/positions"
              submitLabel="Save Changes"
              defaultValues={{
                name: position.name || '',
                category: position.category || 'GeoPosition',
                position_type: position.position_type || '',
                hole_id: position.hole_id?.toString() || '',
                lat: position.lat || '',
                lng: position.lng || '',
                walk_time_minutes: position.walk_time_minutes || '',
                order_promo_text: position.order_promo_text || '',
                sponsor_ids: position.sponsor_ids || [],
                table_number: position.table_number || '',
                capacity: position.capacity || '',
                area: position.area || ''
              }}
            >
              {({ control, errors, watch, setValue }) => {
                const lat = watch('lat')
                const lng = watch('lng')

                return (
                <>
                  {/* Category badge - read-only on edit */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      isIndoor
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {position.category_label}
                    </span>
                    <p className="mt-1 text-sm text-gray-500">
                      {isIndoor ? 'Indoor/table location' : 'Outdoor golf course location'}
                    </p>
                  </div>

                  <FormFieldController
                    control={control}
                    name="name"
                    label="Name"
                    required
                    errors={errors}
                  />

                  <FormFieldController
                    control={control}
                    name="position_type"
                    label="Type"
                    type="select"
                    options={getPositionTypeOptions()}
                    required
                    errors={errors}
                  />

                  {/* GeoPosition-specific fields */}
                  {!isIndoor && (
                    <>
                      <FormFieldController
                        control={control}
                        name="hole_id"
                        label="Associated Hole"
                        type="select"
                        options={holeOptions}
                        hint="Optional - link this location to a specific hole"
                        errors={errors}
                      />

                      <MapLocationPicker
                        lat={lat}
                        lng={lng}
                        onChange={({ lat, lng }) => { setValue('lat', lat); setValue('lng', lng) }}
                        label="Location"
                        hint="Pan and zoom the map to set coordinates"
                        error={errors.lat?.message || errors.lng?.message}
                      />

                      <FormFieldController
                        control={control}
                        name="walk_time_minutes"
                        label="Walk Time (minutes)"
                        type="number"
                        hint="Minutes to walk from this location to catering - used to preset pickup time"
                        errors={errors}
                      />
                    </>
                  )}

                  {/* IndoorPosition-specific fields */}
                  {isIndoor && (
                    <>
                      <FormFieldController
                        control={control}
                        name="table_number"
                        label="Table Number"
                        required
                        placeholder="e.g., T1, A5, Booth 3"
                        hint="Unique identifier for this table/seating area"
                        errors={errors}
                      />

                      <div className="grid grid-cols-2 gap-4">
                        <FormFieldController
                          control={control}
                          name="capacity"
                          label="Capacity"
                          type="number"
                          placeholder="e.g., 4"
                          hint="Maximum number of guests"
                          errors={errors}
                        />
                        <FormFieldController
                          control={control}
                          name="area"
                          label="Area/Section"
                          placeholder="e.g., Terrace, Main Floor"
                          hint="Location within the venue"
                          errors={errors}
                        />
                      </div>
                    </>
                  )}

                  {sponsors.length > 0 && (
                    <FormFieldController
                      control={control}
                      name="sponsor_ids"
                      label="Sponsors"
                      type="searchableCheckboxGroup"
                      options={sponsors}
                      placeholder="Search sponsors..."
                      emptyMessage="No sponsors found"
                      errors={errors}
                    />
                  )}

                  <FormFieldController
                    control={control}
                    name="order_promo_text"
                    label="Order Promo Text"
                    type="textarea"
                    rows={2}
                    hint="Optional message shown to customers ordering from this location"
                    errors={errors}
                  />
                </>
              )}}
            </ResourceForm>
          </div>
        </div>

        {/* QR Code Panel */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">QR Code</h3>

            {/* QR Code Display */}
            <div className="flex justify-center mb-4">
              <div
                className="bg-white p-4 border rounded-lg w-48 h-48 [&>svg]:w-full [&>svg]:h-full"
                dangerouslySetInnerHTML={{ __html: position.qr_code_svg }}
              />
            </div>

            {/* QR Code Info */}
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Code</label>
                <p className="mt-1">
                  <code className="text-sm bg-gray-100 px-2 py-1 rounded">{position.qr_code}</code>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500">Order URL</label>
                <p className="mt-1 text-sm text-gray-900 break-all">
                  <a
                    href={position.qr_scan_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sky-600 hover:text-sky-500"
                  >
                    {position.qr_scan_url}
                  </a>
                </p>
              </div>
            </div>

            {/* Download Button */}
            <button
              type="button"
              onClick={handleDownloadQR}
              className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
            >
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download QR Code
            </button>

            {/* Print Poster Buttons */}
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm font-medium text-gray-700 mb-3">Print Poster</p>
              <div className="grid grid-cols-2 gap-2">
                <Link
                  href={`/manage/positions/${position.id}/print?size=a4`}
                  className="inline-flex justify-center items-center px-3 py-2 border border-sky-500 shadow-sm text-sm font-medium rounded-md text-sky-600 bg-white hover:bg-sky-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  A4
                </Link>
                <Link
                  href={`/manage/positions/${position.id}/print?size=a5`}
                  className="inline-flex justify-center items-center px-3 py-2 border border-sky-500 shadow-sm text-sm font-medium rounded-md text-sky-600 bg-white hover:bg-sky-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  A5
                </Link>
              </div>
            </div>

            <p className="mt-3 text-xs text-gray-500 text-center">
              Print this QR code and place it at the location. Customers can scan it to order.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  )
}
